/**
 * Form primitives for the packages/services screens, written in the CRM's own
 * vocabulary rather than the shared `@wa/ui` chrome.
 *
 * The shared plan editor draws its fields with `MultilingualField` (Radix popover,
 * Tailwind card look). The CRM cannot use those without importing a second design
 * language into a page that is otherwise all sunken pills, so this file rebuilds
 * the same three jobs - label a field, hold one line or one paragraph of text,
 * switch the content locale - out of `crm-lab`, `crm-inp`, `crm-area` and
 * `PillTabs`. No new CSS: every class here already exists in `src/index.css`.
 */
import type { ReactNode } from "react";
import { cn, deriveFilled, type LocaleMap } from "@wa/ui";
import { PillTabs } from "../../../ui";

/** A label over a field, plus an optional note under it. */
export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <>
      <label className="crm-lab">{label}</label>
      {children}
      {hint ? <p className="crm-fnote mt-1">{hint}</p> : null}
    </>
  );
}

/**
 * One sunken pill holding a plain input.
 *
 * `min-w-0` sits on the LABEL on purpose: `index.css` already zeroes the inner
 * input's min-width and that alone does not help, because the flex/grid cell
 * sizes itself from the label. Without it a number input demands roughly 218px
 * inside a 0px track and paints over its neighbour.
 */
export function Pill({
  value,
  onChange,
  placeholder,
  type,
  inputMode,
  disabled,
  maxLength,
  min,
  max,
  step,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  inputMode?: "numeric" | "email" | "tel" | "url";
  disabled?: boolean;
  maxLength?: number;
  min?: string;
  max?: string;
  step?: string;
  className?: string;
}) {
  return (
    <label className={cn("crm-inp min-w-0", disabled && "opacity-60", className)}>
      <input
        type={type ?? "text"}
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        min={min}
        max={max}
        step={step}
        autoComplete="off"
      />
    </label>
  );
}

/**
 * The multi-line sibling of `Pill`. It uses `crm-area` rather than `crm-inp`,
 * because `crm-inp` is a fixed 52px flex row and its child rules only reach
 * `input` and `select` - a textarea inside it would keep its browser border and
 * be squashed to one line. `crm-area` is the same sunken surface in paragraph form.
 */
export function AreaPill({
  value,
  onChange,
  rows,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <textarea
      className="crm-area min-w-0"
      rows={rows ?? 3}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

/**
 * Content-locale switcher for a group of `LocaleField`s.
 *
 * The shared `LangTabs` puts a dot next to each locale: green when that language
 * already has text, grey when it does not, so you can see at a glance which
 * translation is still missing. Colour alone cannot carry that here (a pill tab
 * flips to the inverted surface when selected, which would swallow a tinted dot),
 * so the same fact is spelled with the glyph instead: a full circle means the
 * locale has content, a hollow one means it is still empty.
 */
export function LangSwitch({
  active,
  onChange,
  locales,
  filled,
}: {
  active: string;
  onChange: (l: string) => void;
  locales: readonly string[];
  filled: Record<string, boolean>;
}) {
  if (locales.length < 2) return null;
  return (
    <PillTabs
      small
      value={active}
      onChange={onChange}
      tabs={locales.map((loc) => ({
        value: loc,
        label: `${loc.toUpperCase()} ${filled[loc] ? "●" : "○"}`,
      }))}
    />
  );
}

/**
 * Edits ONE locale of a `LocaleMap`. Unlike the shared `MultilingualField` there
 * is no lock that mirrors the typing into every language: each locale is edited
 * on its own, and `LangSwitch` above shows which ones are still empty.
 */
export function LocaleField({
  label,
  value,
  onChange,
  activeLocale,
  as = "input",
  rows,
  required,
  placeholder,
}: {
  label: string;
  value: LocaleMap;
  onChange: (v: LocaleMap) => void;
  activeLocale: string;
  as?: "input" | "textarea";
  rows?: number;
  required?: boolean;
  placeholder?: string;
}) {
  const current = value[activeLocale] ?? "";
  const set = (next: string) => onChange({ ...value, [activeLocale]: next });

  return (
    <Field label={required ? `${label} *` : label}>
      {as === "textarea" ? (
        <AreaPill value={current} onChange={set} rows={rows} placeholder={placeholder} />
      ) : (
        <Pill value={current} onChange={set} placeholder={placeholder} />
      )}
    </Field>
  );
}

/**
 * Which locales already carry content, across any number of maps. Thin wrapper
 * over the shared `deriveFilled` so the CRM and the admin agree on what "filled"
 * means (a non-blank string in at least one of the maps).
 */
export function deriveFilledMap(locales: readonly string[], ...maps: LocaleMap[]): Record<string, boolean> {
  return deriveFilled(locales, ...maps);
}
